/* eslint-disable no-shadow */
import React from 'react';
import { Link } from 'react-router-dom';
import Dotdotdot from 'react-dotdotdot';
import { useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import { Description } from '../../text';
import BeginSection from './ListViewComponents/BeginSection';
import EndSection from './ListViewComponents/EndSection';
import MessageButton from './ListViewComponents/MessageButton';
import RowPlayerBookmark from '../../SavedLists/PlayerBookmark/RowPlayerBookmark';
import PopOverMenu from '../../PopOverMenu';
import AvatarV2 from './ListViewComponents/AvatarV2';
import PlayerDetails from './ListViewComponents/PlayerDetails';
import Rating from './SharedComponents/Rating';
import Img from '../../Img';
import { sportTypeIds, utrTypeIds } from '../../../utils/eventConstants';
import { getLabelByTypeId } from '../../../utils/types';
import { getPlayerListViewColumnGrid } from '../../../utils';
import { gameTypes, CLUB_PROFILE_VIEW_TYPES } from '../../../utils/constants';
import { isNewRosterModelEnabled } from '../../../utils/statsig';
import { getUserAgeAtEventStart } from '../../../utils/events';

const ListViewEndSection = props => {
    const {
        playerId,
        inactive,
        showBookmark,
        savedBookmark,
        bookmarkTestId,
        showMessage,
        onMessageClick,
        menuOptions,
        onMenuSelected,
        addOptionsSpace,
        optionSpaceContent,
        showSeeProfileLink,
        'data-testid': dataTestId,
    } = props;
    const showMenuOptions = !inactive && menuOptions && menuOptions.length > 0;
    const showOptionsSpaceContent = !!optionSpaceContent;
    if (menuOptions && (addOptionsSpace || showSeeProfileLink || showOptionsSpaceContent)) {
        // warn('Menu Options and another optional content component were supplied, only Mene Options will be shown');
    }

    return (
        <EndSection className={savedBookmark?.enabled ? 'has-save-bookmark' : ''} data-testid={dataTestId}>
            {showBookmark && !savedBookmark?.enabled && <i className="utr utr-bookmark" />}
            {showMessage && <MessageButton onMessageClick={onMessageClick} data-testid={dataTestId && `${dataTestId}.message-btn`} />}
            {showMenuOptions && (
                <PopOverMenu
                    horizontalEllipsis={false}
                    menuItems={menuOptions}
                    onSelected={onMenuSelected}
                    data-testid={dataTestId && `${dataTestId}.popover-menu`}
                />
            )}
            {!menuOptions && (
                <>
                    {showSeeProfileLink && (
                        <Link to={`/profiles/${playerId}`} target="_blank" data-testid={dataTestId && `${dataTestId}.view-profile-link`}>
                            <span className={`extra-card-link`}>View Profile</span>
                        </Link>
                    )}
                    {addOptionsSpace && (
                        <div className="options-space" />
                    )}
                    {showOptionsSpaceContent && (
                        <div className="fs14" data-testid={dataTestId && `${dataTestId}.option-space`}>{optionSpaceContent}</div>
                    )}
                </>
            )}
            <RowPlayerBookmark savedBookmark={savedBookmark} data-testid={bookmarkTestId} />
        </EndSection>
    );
};

const ListViewBeginSection = props => {
    const {
        rank,
        player,
        shrink,
        withdrawTag,
        showOrganizerViewForPTTEvent,
        'data-testid': dataTestId,
    } = props;
    const { firstName, lastName, nationality, playerProfileImages, descriptionItems, college, gradYear } = player;
    return (
        <BeginSection shrink={shrink}>
            {!!rank && <div className="rank" data-testid={dataTestId && `${dataTestId}.rank`}>{withdrawTag || rank}</div>}
            <AvatarV2 playerImages={playerProfileImages} nationality={nationality} player={player} />
            <PlayerDetails
                firstName={firstName}
                lastName={lastName}
                description={descriptionItems}
                showOrganizerViewForPTTEvent={showOrganizerViewForPTTEvent}
                college={college}
                gradYear={gradYear}
                data-testid={dataTestId && `${dataTestId}.details`}
            />
        </BeginSection>
    );
};

const ListView = props => {
    const {
        user,
        hideAlways,
        hideRatings,
        player,
        rank,
        enableMessaging,
        addOptionsSpace,
        utrType,
        addMemberLink,
        paymentStatus,
        paymentStatusTypeId,
        showPlayerPaymentStatus,
        regStatus,
        regStatusColor,
        showRegStatus,
        showRatingNotLoggedIn,
        clubProfileViewType,
        showClubCollegeDetails,
        showSeeProfileLink,
        showDecimals,
        menuOptions,
        optionSpaceContent,
        withdrawTag,
        inactive,
        onMenuSelected,
        onMessageClick,
        ptsRank,
        isWTARankType,
        showAdditionalInformation,
        showProjectedViewStatus,
        showPickleBallRatings = true,
        showTennisRatings = true,
        hideUtrLabel,
        sportTypeId,
        sportTypes,
        isItGlobalSearch,
        showOrganizerViewForPTTEvent,
        eventStartUtc,
        checkGate,
        featureGates,
        savedBookmark,
        'data-testid': dataTestId,
    } = props;

    let tennis;
    let pickleball;

    if (sportTypes) {
        ({ tennis, pickleball } = sportTypes);
    }

    const {
        id,
        ratingStatusSingles,
        ratingStatusDoubles,
        descriptionLong,
        descriptionShort,
        playerCollegeDetails,
        playerHighSchoolDetails,
        classYearStatus,
    } = player;
    const showBoomark = false;
    const isCollegeViewType = clubProfileViewType === CLUB_PROFILE_VIEW_TYPES.COLLEGE;
    const isNewRosterModel = isNewRosterModelEnabled(checkGate, featureGates, isCollegeViewType);

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { tms: { division: { paymentStatusTypes = [] } = {} } = {} } = coreUI.types || {};

    const gridLayout = getPlayerListViewColumnGrid({
        hideRatings,
        pickleball,
        tennis,
        showTennisRatings: isItGlobalSearch ? false : showTennisRatings,
        showPickleBallRatings: isItGlobalSearch ? false : showPickleBallRatings,
        isPtsEvent: !!ptsRank,
        isWTARankType,
        showAdditionalInformation,
        clubProfileViewType,
        addMemberLink,
        showClubCollegeDetails,
        descriptionShort,
        showPlayerPaymentStatus,
        showRegStatus,
        showOrganizerViewForPTTEvent,
    });

    let paymentStatusDisplay = paymentStatus;
    if (paymentStatusTypeId) {
        paymentStatusDisplay = getLabelByTypeId(paymentStatusTypes, paymentStatusTypeId);
    }
    const singlePkbRatings = player.singlespkbrating || player.historicpkbsinglesrating || player.singlesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const doublesPkbRatings = player.doublespkbrating || player.historicpkbdoublesrating || player.doublesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const hidePlayer = !tennis && !!pickleball && !singlePkbRatings && !doublesPkbRatings;
    const age = getUserAgeAtEventStart(player.birthDate, eventStartUtc);

    return (
        <div>
            {!hidePlayer && (
                <div className={`list-view ${hideAlways ? 'd-none' : 'd-none d-md-block'}`}>
                    <div className="d-flex">
                        <div className="flex-grow">
                            <div className="row">
                                <ListViewBeginSection
                                    rank={rank}
                                    shrink={!hideRatings}
                                    player={player}
                                    withdrawTag={withdrawTag}
                                    showOrganizerViewForPTTEvent={showOrganizerViewForPTTEvent}
                                    data-testid={dataTestId && `${dataTestId}.begin-section`}
                                />
                                <div className={`${hideRatings ? 'col-4' : 'col-7'}`}>
                                    <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                                        {showOrganizerViewForPTTEvent && (
                                            <div className={`d-flex aic`}>
                                                <div className="values">
                                                    <div
                                                        className={'value'}
                                                        title={`Players age at start of event`}
                                                        data-testid={dataTestId && `${dataTestId}.age`}
                                                    >
                                                        {age || ``}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {isWTARankType ? (
                                            <>
                                                {ptsRank && (
                                                    <div className={`d-flex aic`}>
                                                        <div className="values">
                                                            <div
                                                                className={'value'}
                                                                title={(utrType === 'sanctioned' || utrType === utrTypeIds.SANCTIONED) ? ratingStatusDoubles : ''}
                                                                data-testid={dataTestId && `${dataTestId}.wta-pts-rank`}
                                                            >
                                                                {ptsRank}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className={`d-flex aic`}>
                                                    <div className="values text-lighter" data-testid={dataTestId && `${dataTestId}.wta-order`}>
                                                        {player.wtaOrder}
                                                    </div>
                                                </div>
                                                <div className={``}>
                                                    {(user || showRatingNotLoggedIn) && (
                                                        <Rating
                                                            utrType={utrType}
                                                            ratingType={gameTypes.SINGLES}
                                                            ratingStatus={ratingStatusSingles}
                                                            player={player}
                                                            showDecimals={showDecimals}
                                                            showProjectedViewStatus={showProjectedViewStatus}
                                                            hideUtrLabel={hideUtrLabel}
                                                            sportTypeId={sportTypeId}
                                                            data-testid={dataTestId && `${dataTestId}.tennis-singles-rating`}
                                                        />
                                                    )}
                                                </div>
                                                <div className={`d-flex aic`}>
                                                    <div className="values text-lighter" data-testid={dataTestId && `${dataTestId}.utr-order`}>
                                                        {player.utrOrder}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            !hideRatings && (
                                                <>
                                                    {showRegStatus && !!regStatus && (
                                                        <div className={`d-flex aic`}>
                                                            <div className={`values ${regStatusColor}`} data-testid={dataTestId && `${dataTestId}.reg-status`}>
                                                                {regStatus}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {((showTennisRatings && tennis && !!isItGlobalSearch) || (showTennisRatings && !isItGlobalSearch)) && (
                                                        <div>
                                                            {(user || showRatingNotLoggedIn) && (
                                                                <Rating
                                                                    utrType={utrType}
                                                                    ratingType={gameTypes.SINGLES}
                                                                    ratingStatus={ratingStatusSingles}
                                                                    player={player}
                                                                    showDecimals={showDecimals}
                                                                    showProjectedViewStatus={showProjectedViewStatus}
                                                                    hideUtrLabel={hideUtrLabel}
                                                                    sportTypeId={sportTypeId}
                                                                    isItGlobalSearch={isItGlobalSearch}
                                                                    data-testid={dataTestId && `${dataTestId}.tennis-singles-rating`}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {((showTennisRatings && tennis && !!isItGlobalSearch) || (showTennisRatings && !isItGlobalSearch)) && (
                                                        <div>
                                                            {(user || showRatingNotLoggedIn) && (
                                                                <Rating
                                                                    utrType={utrType}
                                                                    ratingType={gameTypes.DOUBLES}
                                                                    ratingStatus={ratingStatusDoubles}
                                                                    player={player}
                                                                    showDecimals={showDecimals}
                                                                    showProjectedViewStatus={showProjectedViewStatus}
                                                                    hideUtrLabel={hideUtrLabel}
                                                                    sportTypeId={sportTypeId}
                                                                    isItGlobalSearch={isItGlobalSearch}
                                                                    data-testid={dataTestId && `${dataTestId}.tennis-doubles-rating`}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {((showPickleBallRatings && pickleball && !!isItGlobalSearch) || (showPickleBallRatings && !isItGlobalSearch)) && (

                                                        <>
                                                            <div>
                                                                {(user || showRatingNotLoggedIn) && (
                                                                    <Rating
                                                                        utrType={utrType}
                                                                        ratingType={gameTypes.SINGLES}
                                                                        player={player}
                                                                        showDecimals={showDecimals}
                                                                        showProjectedViewStatus={showProjectedViewStatus}
                                                                        hideUtrLabel={hideUtrLabel}
                                                                        sportTypeId={sportTypeId}
                                                                        isPickleballRating={true}
                                                                        isItGlobalSearch={isItGlobalSearch}
                                                                        data-testid={dataTestId && `${dataTestId}.pkb-singles-rating`}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <Rating
                                                                    utrType={utrType}
                                                                    ratingType={gameTypes.DOUBLES}
                                                                    player={player}
                                                                    showDecimals={showDecimals}
                                                                    showProjectedViewStatus={showProjectedViewStatus}
                                                                    hideUtrLabel={hideUtrLabel}
                                                                    sportTypeId={sportTypeId}
                                                                    isPickleballRating={true}
                                                                    isItGlobalSearch={isItGlobalSearch}
                                                                    data-testid={dataTestId && `${dataTestId}.pkb-doubles-rating`}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                    {!!ptsRank && (
                                                        <div className={`d-flex aic`}>
                                                            <div className="values">
                                                                <div
                                                                    className={'value'}
                                                                    title={(utrType === 'sanctioned' || utrType === utrTypeIds.SANCTIONED) ? ratingStatusDoubles : ''}
                                                                    data-testid={dataTestId && `${dataTestId}.pts-rank`}
                                                                >
                                                                    {ptsRank}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        )}
                                        {clubProfileViewType && (
                                            <>
                                                <div className={`d-flex aic`}>
                                                    <div className="values" data-testid={dataTestId && `${dataTestId}.class-year`}>
                                                        {clubProfileViewType === CLUB_PROFILE_VIEW_TYPES.COLLEGE && (
                                                            isNewRosterModel && classYearStatus
                                                                ? classYearStatus
                                                                : playerCollegeDetails?.gradClassName
                                                        )}
                                                        {clubProfileViewType === CLUB_PROFILE_VIEW_TYPES.HIGHSCHOOL && (
                                                            isNewRosterModel && classYearStatus
                                                                ? classYearStatus
                                                                : playerHighSchoolDetails?.gradClassName
                                                        )}
                                                    </div>
                                                </div>
                                                {addMemberLink && (
                                                    <div className={`d-flex aic`}>
                                                        <Link to={`${addMemberLink}`} data-testid={dataTestId && `${dataTestId}.add-member-link`}>Action Required: Complete Profile</Link>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {isCollegeViewType && (
                                            <div className={`d-flex aic jcc`}>
                                                <div className="values">
                                                    <Img src="/img/v2/icons/icon-paid-hit.svg" />
                                                </div>
                                            </div>
                                        )}
                                        {showClubCollegeDetails && !clubProfileViewType && descriptionShort?.length > 0 && (
                                            <div className="">
                                                <Dotdotdot clamp={2} title={descriptionLong}>
                                                    <Description desc={descriptionShort} noellipsis={true} data-testid={dataTestId && `${dataTestId}.short-description`} />
                                                </Dotdotdot>
                                            </div>
                                        )}
                                        {showPlayerPaymentStatus && (
                                            <div className={`d-flex aic`}>
                                                <div className="values text-lighter" data-testid={dataTestId && `${dataTestId}.payment-status`}>
                                                    {paymentStatusDisplay}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ListViewEndSection
                            playerId={id}
                            showBookmark={showBoomark}
                            savedBookmark={savedBookmark}
                            bookmarkTestId={dataTestId && `${dataTestId}.player-bookmark`}
                            showMessage={enableMessaging}
                            addOptionsSpace={addOptionsSpace}
                            menuOptions={menuOptions}
                            optionSpaceContent={optionSpaceContent}
                            inactive={inactive}
                            showSeeProfileLink={showSeeProfileLink}
                            onMenuSelected={onMenuSelected}
                            onMessageClick={onMessageClick}
                            data-testid={dataTestId && `${dataTestId}.end-section`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

ListViewBeginSection.propTypes = {
    rank: PropTypes.number,
    player: PropTypes.objectOf(any).isRequired,
    shrink: PropTypes.bool.isRequired,
    withdrawTag: PropTypes.string,
    showOrganizerViewForPTTEvent: PropTypes.bool,
    'data-testid': PropTypes.string,
};

ListViewBeginSection.defaultProps = {
    rank: 0,
    withdrawTag: '',
    showOrganizerViewForPTTEvent: false,
    'data-testid': null,
};

ListView.propTypes = {
    showPickleBallRatings: PropTypes.bool,
    sportTypes: PropTypes.instanceOf(Object),
    hideAlways: PropTypes.bool,
    hideRatings: PropTypes.bool,
    rank: PropTypes.number,
    enableMessaging: PropTypes.bool,
    addOptionsSpace: PropTypes.bool,
    utrType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    addMemberLink: PropTypes.bool,
    paymentStatus: PropTypes.string,
    paymentStatusTypeId: PropTypes.number,
    showPlayerPaymentStatus: PropTypes.bool,
    regStatus: PropTypes.string,
    regStatusColor: PropTypes.string,
    showRegStatus: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    clubProfileViewType: PropTypes.string,
    showClubCollegeDetails: PropTypes.bool,
    showSeeProfileLink: PropTypes.bool,
    showDecimals: PropTypes.bool,
    optionSpaceContent: PropTypes.string,
    withdrawTag: PropTypes.string,
    inactive: PropTypes.bool,
    onMenuSelected: PropTypes.func,
    onMessageClick: PropTypes.func,
    ptsRank: PropTypes.number,
    isWTARankType: PropTypes.bool,
    showAdditionalInformation: PropTypes.bool,
    showProjectedViewStatus: PropTypes.bool,
    showTennisRatings: PropTypes.bool,
    hideUtrLabel: PropTypes.bool,
    sportTypeId: PropTypes.number,
    isItGlobalSearch: PropTypes.bool,
    showOrganizerViewForPTTEvent: PropTypes.bool,
    eventStartUtc: PropTypes.string,
    savedBookmark: PropTypes.instanceOf(Object),
    player: PropTypes.objectOf(any).isRequired,
    'data-testid': PropTypes.string,
};

ListView.defaultProps = {
    sportTypes: {},
    hideAlways: false,
    hideRatings: false,
    enableMessaging: false,
    showPlayerPaymentStatus: false,
    showRegStatus: false,
    showRatingNotLoggedIn: false,
    showClubCollegeDetails: false,
    showSeeProfileLink: false,
    showDecimals: false,
    inactive: false,
    isWTARankType: false,
    showAdditionalInformation: false,
    showProjectedViewStatus: false,
    showPickleBallRatings: false,
    showTennisRatings: false,
    hideUtrLabel: false,
    addOptionsSpace: false,
    addMemberLink: false,
    rank: 0,
    utrType: null,
    paymentStatus: null,
    paymentStatusTypeId: 0,
    regStatus: null,
    regStatusColor: null,
    clubProfileViewType: '',
    optionSpaceContent: '',
    withdrawTag: '',
    onMenuSelected: () => {},
    onMessageClick: () => {},
    ptsRank: 0,
    sportTypeId: sportTypeIds.TENNIS,
    isItGlobalSearch: false,
    showOrganizerViewForPTTEvent: false,
    eventStartUtc: null,
    savedBookmark: null,
    'data-testid': null,
};

export default ListView;
